import { useEffect, useState } from 'react';

import {
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    Typography,
    styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useContainer } from 'unstated-next';

import AdvancedOptions from './FormComponents/AdvancedOptions';
import ConsumerKey from './FormComponents/ConsumerKey';
import LmsOptions from './FormComponents/LMSOptions';
import LTI13Settings from './FormComponents/LTI13Settings';
import LTI13Urls from './FormComponents/LTI13Urls';
import LTIVersionText from './FormComponents/LTIVersion';
import NameAndLMS from './FormComponents/NameInputAndLMSSelect';
import RSAPublicKey from './FormComponents/RSAPublicKey';
import SharedSecret from './FormComponents/SharedSecret';
import URLValue from './FormComponents/URLValue';
import VILTSettings from './FormComponents/VILTSettings';
import XMLView from './FormComponents/XMLView';
import IntegrationsHeader from './IntegrationsHeader';
import { LmsTypeCapitalized } from '../../../../components/classroom/types';
import { StyledLoadingButton } from '../../../../components/forms/LoadingButton';
import AdminIntegrationsContainer, { LTIVersion } from '../../AdminIntegrationsContainer';

const StyledFormDiv = styled(Box)`
    display: flex;
    flex-direction: column;
`;

const ActionButtonsDiv = styled(Box)`
    display: flex;
    margin-top: 24px;
`;

const StyledSaveButtonDiv = styled(Box)`
    margin-left: 10px;
    display: flex;
    justify-content: center;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
    margin-top: 14px;
`;

interface IntegrationEditProps {
    integration: string;
}

const IntegrationEdit = () => {
    const { t } = useTranslation('admin_settings');

    const {
        selectedIntegration,
        updateIntegration,
        deleteIntegration,
        fetchIntegrationList,
        setIntegrationId,
        integrationId,
        isLoading
    } = useContainer(AdminIntegrationsContainer);
    const [formState, setFormState] = useState<any>(selectedIntegration);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const { path, params } = useRouteMatch<IntegrationEditProps>();
    useEffect(() => {
        if (params.integration) {
            setIntegrationId(params.integration);
        }
    }, [path, params, setIntegrationId]);

    const history = useHistory();
    const checkForEmptyValues = () => {
        const {
            title,
            type: integrationType,
            lti_version: version,
            consumer_client_id: clientId,
            consumer_authorization_url: authUrl,
            consumer_access_token_url: tokenUrl,
            consumer_authorization_config_url: authConfigUrl
        } = formState || {};

        if (!title || !integrationType) {
            return true;
        }

        return version === '1.3' && (!clientId || !authUrl || !tokenUrl || !authConfigUrl);
    };

    useEffect(() => {
        if (selectedIntegration) {
            setFormState(selectedIntegration);
        }
    }, [selectedIntegration, fetchIntegrationList, setIntegrationId, integrationId, isLoading]);
    const previousPath = path.replace(/\/edit\/.*/, '');
    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        if (['debug_mode', 'grant_instructor_system_role'].includes(name)) {
            setFormState((prevState) => ({
                ...prevState,
                [name]: checked
            }));
            return;
        }
        setFormState({
            ...formState,
            [name]: value
        });
    };

    const handleDelete = async (event) => {
        event.preventDefault();
        setDeleting(true);
        await deleteIntegration();
        await fetchIntegrationList();
        setDeleting(false);
        history.push(previousPath);
    };

    const handleDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        console.log('saving formstate', formState);
        await updateIntegration(formState);
        await fetchIntegrationList();
        setSaving(false);
    };

    const LTI11Options = () => (
        <>
            <ConsumerKey consumerKey={formState?.client_id} />
            <SharedSecret sharedSecret={formState?.shared_secret} />
            <URLValue />
            <XMLView title={formState?.title} />
        </>
    );

    const {
        type: integrationType,
        lti_creds_uuid: ltiCredsUuid,
        lti_version: ltiVersion,
        learn_app_key: consumerAppKey,
        learn_app_secret: consumerSecret,
        consumer_client_id: consumerClientId,
        consumer_authorization_url: consumerAuthorizationUrl,
        consumer_access_token_url: consumerAccessTokenUrl,
        consumer_authorization_config_url: consumerAuthorizationConfigUrl,
        rsa_public_key: rsaPublicKey,
        person_id_prefix: personIdPrefix,
        course_id_prefix: courseIdPrefix,
        course_id_attribute_name: courseIdAttributeName,
        person_id_attribute_name: personIdAttributeName,
        email_attribute_name: emailAttributeName,
        debug_mode: debugMode,
        type: selectedLMS,
        lms_base_url: lmsBaseUrl,
        lms_key: lmsKey,
        lms_secret: lmsSecret,
        admin_username: adminUsername,
        sync_settings: syncSettings,
        grant_instructor_system_role: grantInstructorSystemRole,
        config_data: extraAdminConfigProperties
    } = formState || {};
    const isLTI13 = ltiVersion === LTIVersion.V1_3;

    const hasLTI = integrationType !== LmsTypeCapitalized.CSOD;
    const hasVLTI = integrationType === LmsTypeCapitalized.CSOD;

    return (
        <>
            <IntegrationsHeader />

            <StyledBreadcrumbs>
                <Link variant="body1" underline="hover" color="primary" onClick={() => history.goBack()}>
                    {t('lms-configurations')}
                </Link>

                <Typography variant="body1" color="text.primary">
                    {formState?.title}
                </Typography>
            </StyledBreadcrumbs>

            <StyledFormDiv>
                <NameAndLMS title={formState?.title} type={selectedIntegration?.type} handleChange={handleChange} />
                <LTIVersionText ltiVersion={ltiVersion} />
                {hasVLTI && <VILTSettings lti_creds_uuid={selectedIntegration?.lti_creds_uuid} />}
                {!isLTI13 && hasLTI && <LTI11Options />}
                {isLTI13 && (
                    <>
                        <LTI13Settings
                            integrationType={integrationType}
                            consumerClientId={consumerClientId}
                            consumerAppKey={consumerAppKey}
                            consumerSecret={consumerSecret}
                            consumerAuthorizationUrl={consumerAuthorizationUrl}
                            consumerAccessTokenUrl={consumerAccessTokenUrl}
                            consumerAuthorizationConfigUrl={consumerAuthorizationConfigUrl}
                            selectedLMS={selectedLMS}
                            handleChange={handleChange}
                        />
                        <LTI13Urls selectedLMS={selectedLMS} ltiCredsUuid={ltiCredsUuid} />
                        <RSAPublicKey rsaPublicKey={rsaPublicKey} />
                    </>
                )}

                <AdvancedOptions
                    emailAttributeName={emailAttributeName}
                    courseIdAttributeName={courseIdAttributeName}
                    courseIdPrefix={courseIdPrefix}
                    personIdAttributeName={personIdAttributeName}
                    personIdPrefix={personIdPrefix}
                    hasLTI={hasLTI}
                    debugMode={debugMode}
                    grantInstructorSystemRole={grantInstructorSystemRole}
                    handleChange={handleChange}
                />

                <LmsOptions
                    integrationType={integrationType}
                    lmsBaseUrl={lmsBaseUrl}
                    lmsKey={lmsKey}
                    lmsSecret={lmsSecret}
                    adminUsername={adminUsername}
                    syncSettings={syncSettings}
                    handleChange={handleChange}
                    extraAdminConfigProperties={extraAdminConfigProperties}
                    ltiVersion={ltiVersion}
                />

                <ActionButtonsDiv>
                    <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)}>
                        {t('delete')}
                    </Button>

                    <StyledSaveButtonDiv>
                        <StyledLoadingButton
                            variant="contained"
                            onClick={handleSave}
                            disabled={checkForEmptyValues()}
                            loading={saving}
                        >
                            {t('save')}
                        </StyledLoadingButton>
                    </StyledSaveButtonDiv>
                </ActionButtonsDiv>

                <Dialog open={deleteDialogOpen} onClose={handleDialogClose}>
                    <DialogTitle>{t('delete-integration')}</DialogTitle>

                    <DialogContent>
                        <DialogContentText color="text.primary">
                            {t('confirm-delete', { lmsTitle: formState?.title })}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        <Button variant="outlined" onClick={handleDialogClose}>
                            {t('cancel')}
                        </Button>
                        <StyledLoadingButton
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            loading={deleting}
                        >
                            {t('delete')}
                        </StyledLoadingButton>
                    </DialogActions>
                </Dialog>
            </StyledFormDiv>
        </>
    );
};

export default IntegrationEdit;
